import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from './PageHero';
import Icon from './Icons';
import useReveal from './Reveal';
import COMPANY, { ROUTES } from '../../config/company';

/**
 * Shared layout for Privacy Policy and Terms & Conditions.
 *
 * `sections` is an array of [heading, body, id?]. A body may be a string, or
 * an array whose items are paragraph strings, `{ list: [...] }` for bullets,
 * `{ quote: '...' }` for a highlighted sample message, or a React node.
 */
export default function LegalPage({ eyebrow, title, lede, sections }) {
  useReveal();
  return (
    <>
      <PageHero eyebrow={eyebrow} title={title} lede={lede} compact>
        <p className="text-[0.85rem] text-navy-200">
          {COMPANY.legalName} · Last updated {COMPANY.legalUpdated}
        </p>
      </PageHero>

      <section className="py-14 sm:py-20">
        <div className="shell grid gap-10 lg:grid-cols-[1fr_300px] lg:gap-14">
          <div className="prose-legal max-w-3xl">
            <nav aria-label="Contents" className="mb-10 rounded-xl border border-ink-200 bg-ink-50 p-5">
              <div className="text-[0.72rem] font-bold uppercase tracking-[0.16em] text-ink-500">Contents</div>
              <ol className="mt-3 grid gap-1.5 text-[0.88rem] sm:grid-cols-2">
                {sections.map(([heading, , id], i) => (
                  <li key={heading}>
                    <a href={`#${id || slug(heading)}`} className="font-semibold text-navy-800 hover:underline">
                      {i + 1}. {heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {sections.map(([heading, body, id], i) => (
              <section key={heading} id={id || slug(heading)} className="scroll-mt-28">
                <h2 className="flex items-baseline gap-3">
                  <span className="font-display text-base font-bold text-gold-600">{String(i + 1).padStart(2, '0')}</span>
                  {heading}
                </h2>
                <div className="mt-3 space-y-3">
                  <Body body={body} />
                </div>
              </section>
            ))}
          </div>

          <aside>
            <div className="card sticky top-28 p-6">
              <h2 className="font-display text-lg font-bold">Questions about this policy?</h2>
              <p className="mt-2 text-[0.88rem] leading-relaxed text-ink-600">
                Contact our team by email or through our website.
              </p>
              {COMPANY.phone && <a href={COMPANY.phone} className="btn-dark mt-4 w-full">
                <Icon name="phone" className="h-4 w-4" /> {COMPANY.phoneDisplay}
              </a>}
              <a href={COMPANY.email} className="btn-outline mt-2 w-full normal-case tracking-normal">
                {COMPANY.emailDisplay}
              </a>
              <div className="mt-5 border-t border-ink-200 pt-4 text-[0.85rem] text-ink-600">
                <div className="font-bold text-navy-900">{COMPANY.legalName}</div>
                {COMPANY.hq.line1}, {COMPANY.hq.line2}
              </div>
              <div className="mt-5 border-t border-ink-200 pt-4">
                <Link to={ROUTES.privacy} className="block py-1 text-[0.9rem] font-semibold text-navy-800 hover:underline">Privacy Policy</Link>
                <Link to={ROUTES.terms} className="block py-1 text-[0.9rem] font-semibold text-navy-800 hover:underline">Terms &amp; Conditions</Link>
                <Link to={ROUTES.smsOptIn} className="block py-1 text-[0.9rem] font-semibold text-navy-800 hover:underline">SMS opt-in page</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Body({ body }) {
  const items = Array.isArray(body) ? body : [body];
  return items.map((item, i) => {
    if (typeof item === 'string') return <p key={i}>{item}</p>;
    if (React.isValidElement(item)) return <div key={i}>{item}</div>;
    if (item.list) {
      return (
        <ul key={i}>
          {item.list.map((li) => (
            <li key={typeof li === 'string' ? li : i}>{li}</li>
          ))}
        </ul>
      );
    }
    if (item.quote) {
      return (
        <blockquote key={i} className="rounded-xl border-l-4 border-gold-300 bg-ink-50 px-5 py-3 text-[0.92rem] italic text-ink-700">
          {item.quote}
        </blockquote>
      );
    }
    return null;
  });
}

export function slug(s) {
  return s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
